/**
 * Centralised React Query configuration.
 *
 * All query keys and per-query options live here so that any page or
 * component can import the correct settings without duplication.
 * To add a new resource: define its key and options in the relevant
 * section and import them where needed.
 */

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const queryKeys = {
  cameraGroups: {
    all: () => ['camera-groups', 'all'] as const,
  },
  cameras: {
    all: () => ['cameras', 'all'] as const,
    paginated: (search?: string) => ['cameras', 'paginated', search ?? ''] as const,
    infinite: (
      search: string,
      group: string,
      ingestionType: string,
      onlineStatus: string,
      batteryLow: string,
    ) => ['cameras', 'infinite', search, group, ingestionType, onlineStatus, batteryLow] as const,
    summary: () => ['cameras', 'summary'] as const,
    mapPins: () => ['cameras', 'map-pins'] as const,
  },
  events: {
    recent: () => ['events', 'recent'] as const,
    paginated: (filters: object) => ['events', 'paginated', filters] as const,
  },
  detectionClasses: () => ['detection-classes'] as const,
  aiModels: () => ['ai-models'] as const,
  globalThresholds: () => ['global-thresholds'] as const,
  notifications: {
    unread: () => ['notifications', 'unread'] as const,
  },
  cameraExchangeLogs: (params: object) => ['camera-exchange-logs', params] as const,
  mapConfig: {
    active: (platform: 'web' | 'ios' | 'android' = 'web') => ['mapConfig', platform] as const,
  },
}

// ---------------------------------------------------------------------------
// Polling intervals (ms)
// ---------------------------------------------------------------------------

const POLL_DASHBOARD_CAMERAS = 60_000
const POLL_DASHBOARD_EVENTS  = 30_000

// ---------------------------------------------------------------------------
// Per-resource query options
// ---------------------------------------------------------------------------

export const queryOptions = {
  cameraGroups: {
    all: {
      staleTime: 60_000,
    },
  },
  cameras: {
    all: {
      staleTime: 30_000,
      refetchInterval: POLL_DASHBOARD_CAMERAS,
    },
    paginated: {
      staleTime: 30_000,
    },
    infinite: {
      staleTime: 30_000,
      refetchInterval: POLL_DASHBOARD_CAMERAS,
    },
  },
  events: {
    recent: {
      staleTime: 30_000,
      refetchInterval: POLL_DASHBOARD_EVENTS,
    },
    paginated: {
      staleTime: 30_000,
    },
  },
  detectionClasses: {
    staleTime: Infinity,
  },
  aiModels: {
    staleTime: Infinity,
  },
  notifications: {
    unread: {
      staleTime: 30_000,
      refetchInterval: POLL_DASHBOARD_EVENTS,
    },
  },
}
